// ============================================================================
// camera.js — third-person orbit-follow camera with mouse-wheel zoom that runs
// all the way into first-person. Pivots on Benjen's head; yaw/pitch on mouse or
// right-stick; a single snappy position damp (no floaty double-lag); a gentle
// yaw-only auto-recenter that won't fight the player while moving. Sprint kicks
// the FOV; impacts add micro-shake.
// ============================================================================

import * as THREE from 'three';
import {
  CAM_FOV, CAM_DIST, CAM_MIN_DIST, CAM_MAX_DIST, CAM_PIVOT_H,
  CAM_PITCH_DEFAULT, CAM_PITCH_MIN, CAM_PITCH_MAX,
  CAM_POS_STIFF, CAM_PIVOT_STIFF, CAM_LOOK_AHEAD, CAM_RECENTER_RATE,
  CAM_ZOOM_STEP, PLAYER_SPEED, SHAKE_MAX,
} from '../core/constants.js';
import { damp, clamp, fwdX, fwdZ, angleDamp } from '../core/math.js';

const FP_THRESHOLD = 0.7;   // below this orbit distance we're first-person

export class FollowCamera {
  constructor(camera) {
    this.cam = camera;
    this.cam.fov = CAM_FOV;
    this.cam.updateProjectionMatrix();
    this.yaw = Math.PI;
    this.pitch = CAM_PITCH_DEFAULT;
    this.userDist = CAM_DIST;      // wheel-controlled target distance
    this.curDist = CAM_DIST;       // smoothed
    this.pivot = new THREE.Vector3(0, CAM_PIVOT_H, 0);
    this.pos = new THREE.Vector3(0, CAM_PIVOT_H, -CAM_DIST);
    this.lookDir = new THREE.Vector3(0, 0, 1);
    this.recenterHold = 0;
    this.sens = 0.0042;      // snappier look (matches the earlier feel)
    this.invertY = true;     // push mouse up → look up
    this._shakeSeed = 0;
  }

  update(player, input, dt, world) {
    // ---- mouse / right-stick look ----
    const look = input.look;
    if (Math.abs(look.dx) > 1e-4 || Math.abs(look.dy) > 1e-4) {
      this.yaw -= look.dx * this.sens;
      const pv = this.invertY ? 1 : -1;   // flip vertical from the previous (backwards) feel
      this.pitch = clamp(this.pitch + pv * look.dy * this.sens, CAM_PITCH_MIN, CAM_PITCH_MAX);
      this.recenterHold = 1.5;
    }
    if (this.recenterHold > 0) this.recenterHold -= dt;

    // ---- wheel zoom (into first-person) ----
    if (input.wheel !== 0) {
      this.userDist = clamp(this.userDist + input.wheel * CAM_ZOOM_STEP, CAM_MIN_DIST, CAM_MAX_DIST);
      input.wheel = 0;
    }

    // effective distance (Titanfall pulls in a touch for drama)
    const wantDist = this.userDist * (player.state === 'titanfall' ? 0.85 : 1);
    this.curDist = damp(this.curDist, wantDist, 12, dt);
    const firstPerson = this.curDist < FP_THRESHOLD;
    world._firstPerson = firstPerson;

    // ---- gentle auto-recenter behind movement (yaw only, third-person only) ----
    const speed = Math.hypot(player.vx, player.vz);
    if (!firstPerson && this.recenterHold <= 0 && speed > 2.2) {
      const moveHeading = Math.atan2(player.vx, player.vz);
      this.yaw = angleDamp(this.yaw, moveHeading, CAM_RECENTER_RATE * (speed / PLAYER_SPEED), dt);
    }

    // ---- pivot (head), with a gentle forward lead while moving in 3rd person ----
    const lead = firstPerson ? 0 : Math.min(1, speed / PLAYER_SPEED) * CAM_LOOK_AHEAD;
    const px = player.x + fwdX(player.heading) * lead;
    const pz = player.z + fwdZ(player.heading) * lead;
    const py = player.y + CAM_PIVOT_H;
    this.pivot.x = damp(this.pivot.x, px, CAM_PIVOT_STIFF, dt);
    this.pivot.y = damp(this.pivot.y, py, CAM_PIVOT_STIFF, dt);
    this.pivot.z = damp(this.pivot.z, pz, CAM_PIVOT_STIFF, dt);

    // ---- look direction from yaw/pitch; camera sits back along it ----
    const cp = Math.cos(this.pitch), sp = Math.sin(this.pitch);
    this.lookDir.set(Math.sin(this.yaw) * cp, -sp, Math.cos(this.yaw) * cp);

    const dx = this.pivot.x - this.lookDir.x * this.curDist;
    const dy = Math.max(0.5, this.pivot.y - this.lookDir.y * this.curDist); // never dip underground
    const dz = this.pivot.z - this.lookDir.z * this.curDist;
    this.pos.x = damp(this.pos.x, dx, CAM_POS_STIFF, dt);
    this.pos.y = damp(this.pos.y, dy, CAM_POS_STIFF, dt);
    this.pos.z = damp(this.pos.z, dz, CAM_POS_STIFF, dt);

    // ---- impact shake ----
    let sx = 0, sy = 0;
    if (world.shake > 0.001) {
      const s = Math.min(world.shake, SHAKE_MAX);
      this._shakeSeed += dt * 60;
      sx = (Math.sin(this._shakeSeed * 12.9) + Math.sin(this._shakeSeed * 27.1)) * 0.5 * s * 0.25;
      sy = (Math.cos(this._shakeSeed * 17.3) + Math.sin(this._shakeSeed * 9.7)) * 0.5 * s * 0.25;
      world.shake = damp(world.shake, 0, 9, dt);
    }

    this.cam.position.set(this.pos.x + sx, this.pos.y + sy, this.pos.z);
    if (firstPerson) {
      // free-look from the head
      this.cam.lookAt(this.pos.x + this.lookDir.x, this.pos.y + this.lookDir.y, this.pos.z + this.lookDir.z);
    } else {
      // anchored: always keep Benjen framed, so the camera never swings off him
      this.cam.lookAt(this.pivot.x, this.pivot.y, this.pivot.z);
    }

    // ---- FOV: sprint & first-person kick ----
    const fovTarget = CAM_FOV + (player.sprinting ? 8 : 0) + (firstPerson ? 8 : 0) + (player.state === 'titanfall' ? 6 : 0);
    if (Math.abs(this.cam.fov - fovTarget) > 0.05) {
      this.cam.fov = damp(this.cam.fov, fovTarget, 8, dt);
      this.cam.updateProjectionMatrix();
    }
  }
}
